import React, { useState, useMemo, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import {
  MagnifyingGlassIcon,
  EyeIcon,
  PencilSquareIcon,
  TrashIcon,
  PlusIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ChevronDoubleLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import clsx from "clsx";
import { useTable, useSortBy } from "react-table";
import { BASE_URL } from "../../config";

const Badge = ({ children, color = "gray" }) => {
  const colors = {
    gray: "bg-gray-100 text-gray-700 ring-gray-200",
    green: "bg-emerald-100 text-emerald-700 ring-emerald-200",
    blue: "bg-blue-100 text-blue-700 ring-blue-200",
    red: "bg-rose-100 text-rose-700 ring-rose-200",
    yellow: "bg-amber-100 text-amber-700 ring-amber-200",
    slate: "bg-slate-100 text-slate-700 ring-slate-200",
  };
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1",
        colors[color]
      )}
    >
      {children}
    </span>
  );
};

const EmptyState = ({
  title = "No results",
  subtitle = "Try adjusting your search or filters.",
}) => (
  <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 p-10 text-center">
    <svg
      viewBox="0 0 24 24"
      className="mb-2 h-8 w-8 text-gray-400"
      fill="none"
      stroke="currentColor"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
    <div className="text-sm font-medium text-gray-900">{title}</div>
    <div className="text-xs text-gray-500">{subtitle}</div>
  </div>
);

const RefCoverageMasterModal = ({ onClose, data = null, refreshTable }) => {
  const [formData, setFormData] = useState(data || {
    coverage_code: "",
    coverage_desc: "",
    entitlement_transcode: "",
    claims_transcode: "",
    active_flag: null,
    lastupdate_date: null,
    update_by: "",
    billing_ip_or_op: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (data) {
        await axios.put(`${BASE_URL}/ref-coverage-master/${data.tbl_id}`, formData, { headers: getAuthHeaders() });
        toast.success("Record updated successfully");
      } else {
        await axios.post(`${BASE_URL}/ref-coverage-master`, formData, { headers: getAuthHeaders() });
        toast.success("Record added successfully");
      }
      refreshTable();
      onClose();
    } catch (error) {
      toast.error("Failed to save record: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  });

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      >
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.98 }}
          transition={{ type: "spring", stiffness: 260, damping: 22 }}
          className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
        >
          <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-4 py-3 text-white">
            <h2 className="text-base font-semibold">
              {data ? "Edit Record" : "Add New Record"}
            </h2>
          </div>
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Coverage Code</label>
              <input
                type="text"
                value={formData.coverage_code}
                onChange={(e) => setFormData({ ...formData, coverage_code: e.target.value })}
                className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Coverage Description</label>
              <input
                type="text"
                value={formData.coverage_desc}
                onChange={(e) => setFormData({ ...formData, coverage_desc: e.target.value })}
                className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Entitlement Transcode</label>
              <input
                type="text"
                value={formData.entitlement_transcode}
                onChange={(e) => setFormData({ ...formData, entitlement_transcode: e.target.value })}
                className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Claims Transcode</label>
              <input
                type="text"
                value={formData.claims_transcode}
                onChange={(e) => setFormData({ ...formData, claims_transcode: e.target.value })}
                className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Active Flag</label>
              <input
                type="number"
                value={formData.active_flag}
                onChange={(e) => setFormData({ ...formData, active_flag: e.target.value })}
                className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Last Update Date</label>
              <input
                type="text"
                value={formData.lastupdate_date}
                onChange={(e) => setFormData({ ...formData, lastupdate_date: e.target.value })}
                className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Update By</label>
              <input
                type="text"
                value={formData.update_by}
                onChange={(e) => setFormData({ ...formData, update_by: e.target.value })}
                className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Billing IP or OP</label>
              <input
                type="text"
                value={formData.billing_ip_or_op}
                onChange={(e) => setFormData({ ...formData, billing_ip_or_op: e.target.value })}
                className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? "Saving..." : data ? "Update" : "Add"}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const RefCoverageMaster = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("coverage_code");
  const [sortDirection, setSortDirection] = useState("asc");

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteRecordId, setDeleteRecordId] = useState(null);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/ref-coverage-master`, {
        headers: getAuthHeaders(),
        params: {
          limit_offset: (currentPage - 1) * itemsPerPage,
          search_string: searchQuery,
          sort_by: sortBy,
          sort_direction: sortDirection,
          limit: itemsPerPage,
        },
      });
      setData(response.data.data);
      setTotalCount(response.data.total_count);
    } catch (error) {
      toast.error("Failed to fetch data: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchQuery, sortBy, sortDirection]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const columns = useMemo(
    () => [
      { accessor: "tbl_id", Header: "ID", disableSortBy: false },
      { accessor: "coverage_code", Header: "Coverage Code", disableSortBy: false },
      { accessor: "coverage_desc", Header: "Coverage Description", disableSortBy: false },
      { accessor: "entitlement_transcode", Header: "Entitlement Transcode", disableSortBy: false },
      { accessor: "claims_transcode", Header: "Claims Transcode", disableSortBy: false },
      { accessor: "active_flag", Header: "Active Flag", disableSortBy: false },
      { accessor: "lastupdate_date", Header: "Last Update Date", disableSortBy: false },
      { accessor: "update_by", Header: "Update By", disableSortBy: false },
      { accessor: "billing_ip_or_op", Header: "Billing IP or OP", disableSortBy: false },
      {
        Header: "Actions",
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="flex gap-2">
            <button
              onClick={() => {
                setSelectedRecord(row.original);
                setModalOpen(true);
              }}
              className="text-blue-600 hover:text-blue-800"
            >
              <EyeIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => {
                setSelectedRecord(row.original);
                setModalOpen(true);
              }}
              className="text-green-600 hover:text-green-800"
            >
              <PencilSquareIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => {
                setDeleteRecordId(row.original.tbl_id);
                setDeleteModalOpen(true);
              }}
              className="text-red-600 hover:text-red-800"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    { columns, data, manualSortBy: true },
    useSortBy
  );

  const handleDelete = async () => {
    setLoading(true);
    try {
      await axios.delete(`${BASE_URL}/ref-coverage-master/${deleteRecordId}`, { headers: getAuthHeaders() });
      toast.success("Record deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete record: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
      setDeleteModalOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl bg-white rounded-xl shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Ref Coverage Master</h1>
          <button
            onClick={() => {
              setSelectedRecord(null);
              setModalOpen(true);
            }}
            className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5" />
            Add New
          </button>
        </div>

        <div className="mb-4 flex items-center gap-4">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              className="w-full rounded-md border border-gray-200 pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </select>
        </div>

        {loading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-100 rounded"></div>
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState title="No records found" subtitle="Try adjusting your search." />
        ) : (
          <div className="overflow-x-auto">
            <table {...getTableProps()} className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        <div className="flex items-center gap-1">
                          {column.render("Header")}
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <ArrowDownIcon className="h-4 w-4" />
                            ) : (
                              <ArrowUpIcon className="h-4 w-4" />
                            )
                          ) : null}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()} className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalCount)} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="p-2 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronDoubleLeftIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <span className="px-3 py-1.5 text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="p-2 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronDoubleRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {modalOpen && (
        <RefCoverageMasterModal
          onClose={() => setModalOpen(false)}
          data={selectedRecord}
          refreshTable={fetchData}
        />
      )}

      {deleteModalOpen && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          >
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
            >
              <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-4 py-3 text-white">
                <h2 className="text-base font-semibold">Confirm Deletion</h2>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-700 mb-4">Are you sure you want to delete this record?</p>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setDeleteModalOpen(false)}
                    className="rounded-md border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="rounded-md bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700"
                    disabled={loading}
                  >
                    {loading ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
};

export default RefCoverageMaster;